import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, Download, Loader, Plane } from 'lucide-react';
import { tripService, clientService } from '../services/api';
import { format, parseISO } from 'date-fns';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import { openPdfInNewTab } from '../utils/pdfDelivery';
import TripWizard from './TripWizard';

const EMPTY_DAY = {
  date: '',
  departure_time: '',
  end_time: '',
  breakfast_covered: false,
  lunch_covered: false,
  dinner_covered: false,
  overnight: false,
};

const EMPTY_DEST = { client_id: '', location_id: '' };

function TripForm() {
  const isMobile = useIsMobile();
  return isMobile ? <TripWizard /> : <DesktopTripForm />;
}

function DesktopTripForm() {
  const navigate = useNavigate();
  const { toast, confirm } = useDialog();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clients, setClients] = useState([]);

  const [form, setForm] = useState({
    name: '',
    is_foreign_travel: false,
    border_crossing_outbound: '',
    border_crossing_return: '',
    seminar_includes_meals: null,
    notes: '',
    days: [{ ...EMPTY_DAY }],
    destinations: [],
  });

  // PDF generation state
  const [pdfLoading, setPdfLoading] = useState(false);
  const [costOverrides, setCostOverrides] = useState({
    bus_amount: '', taxi_amount: '', nkso1_amount: '',
    nkso2_amount: '', park_amount: '', bewk_amount: '', km_amount: '',
  });
  const [showPdfPanel, setShowPdfPanel] = useState(false);

  useEffect(() => {
    clientService.getClients().then(setClients).catch(console.error);
    if (isEdit) {
      Promise.all([
        tripService.getTrip(id),
        tripService.getComputedCosts(id),
      ])
        .then(([trip, computedCosts]) => {
          setForm({
            name: trip.name,
            is_foreign_travel: trip.is_foreign_travel,
            border_crossing_outbound: trip.border_crossing_outbound
              ? format(parseISO(trip.border_crossing_outbound), "yyyy-MM-dd'T'HH:mm")
              : '',
            border_crossing_return: trip.border_crossing_return
              ? format(parseISO(trip.border_crossing_return), "yyyy-MM-dd'T'HH:mm")
              : '',
            seminar_includes_meals: trip.seminar_includes_meals,
            notes: trip.notes || '',
            days: trip.days.length > 0
              ? trip.days.map(d => ({
                  date: format(parseISO(d.date), 'yyyy-MM-dd'),
                  departure_time: d.departure_time || '',
                  end_time: d.end_time || '',
                  breakfast_covered: d.breakfast_covered,
                  lunch_covered: d.lunch_covered,
                  dinner_covered: d.dinner_covered,
                  overnight: d.overnight,
                }))
              : [{ ...EMPTY_DAY }],
            destinations: trip.destinations.map(d => ({
              client_id: d.client_id,
              location_id: d.location_id || '',
            })),
          });
          setCostOverrides({
            bus_amount:  computedCosts.bus   != null ? String(computedCosts.bus)   : '',
            taxi_amount: computedCosts.taxi  != null ? String(computedCosts.taxi)  : '',
            nkso1_amount: computedCosts.nkso1 != null ? String(computedCosts.nkso1) : '',
            nkso2_amount: computedCosts.nkso2 != null ? String(computedCosts.nkso2) : '',
            park_amount: computedCosts.park  != null ? String(computedCosts.park)  : '',
            bewk_amount: computedCosts.bewk  != null ? String(computedCosts.bewk)  : '',
            km_amount: computedCosts.km    != null ? String(computedCosts.km)    : '',
          });
        })
        .catch(() => navigate('/trips'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // ── Days ──────────────────────────────────────────────────────────────────
  const setDayField = (idx, key, value) =>
    setForm(f => {
      const days = [...f.days];
      days[idx] = { ...days[idx], [key]: value };
      return { ...f, days };
    });

  const addDay = () => {
    const lastDate = form.days[form.days.length - 1]?.date;
    let nextDate = '';
    if (lastDate) {
      const d = new Date(lastDate);
      d.setDate(d.getDate() + 1);
      nextDate = format(d, 'yyyy-MM-dd');
    }
    setForm(f => ({ ...f, days: [...f.days, { ...EMPTY_DAY, date: nextDate }] }));
  };

  const removeDay = (idx) =>
    setForm(f => ({ ...f, days: f.days.filter((_, i) => i !== idx) }));

  // ── Destinations ─────────────────────────────────────────────────────────
  const setDestField = (idx, key, value) =>
    setForm(f => {
      const destinations = [...f.destinations];
      destinations[idx] = { ...destinations[idx], [key]: value };
      if (key === 'client_id') destinations[idx].location_id = '';
      return { ...f, destinations };
    });

  const addDest = () => setForm(f => ({ ...f, destinations: [...f.destinations, { ...EMPTY_DEST }] }));
  const removeDest = (idx) => setForm(f => ({ ...f, destinations: f.destinations.filter((_, i) => i !== idx) }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        is_foreign_travel: form.is_foreign_travel,
        border_crossing_outbound: form.border_crossing_outbound || null,
        border_crossing_return: form.border_crossing_return || null,
        seminar_includes_meals: form.seminar_includes_meals,
        notes: form.notes || null,
        days: form.days.filter(d => d.date).map(d => ({
          date: new Date(d.date).toISOString(),
          departure_time: d.departure_time || null,
          end_time: d.end_time || null,
          breakfast_covered: d.breakfast_covered,
          lunch_covered: d.lunch_covered,
          dinner_covered: d.dinner_covered,
          overnight: d.overnight,
        })),
        destinations: form.destinations
          .filter(d => d.client_id)
          .map(d => ({
            client_id: parseInt(d.client_id),
            location_id: d.location_id ? parseInt(d.location_id) : null,
          })),
      };

      if (isEdit) {
        await tripService.updateTrip(id, payload);
      } else {
        await tripService.createTrip(payload);
      }
      navigate('/trips');
    } catch (err) {
      toast('Error saving trip', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!await confirm({
      title: 'Delete this trip?',
      message: 'This cannot be undone. Linked expenses will not be deleted.',
      confirmLabel: 'Delete',
      destructive: true,
    })) return;
    setDeleting(true);
    try {
      await tripService.deleteTrip(id);
      navigate('/trips');
    } catch (err) {
      toast('Error deleting trip', 'error');
      setDeleting(false);
    }
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const handleGeneratePdf = async () => {
    setPdfLoading(true);
    try {
      const overrides = {};
      Object.entries(costOverrides).forEach(([k, v]) => {
        if (v !== '') overrides[k] = parseFloat(v);
      });
      openPdfInNewTab(await tripService.generatePdf(id, overrides));
    } catch (err) {
      toast('Error generating PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const locationsFor = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    return client?.locations || [];
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/trips')} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Edit Trip' : 'New Trip'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Trip details</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip reason / name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder="e.g. Visit Enfinium Plants in UK"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Seminar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seminar/conference — does registration include free meals?
            </label>
            <div className="flex gap-4">
              {[null, true, false].map(val => (
                <label key={String(val)} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={form.seminar_includes_meals === val}
                    onChange={() => setField('seminar_includes_meals', val)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700">
                    {val === null ? 'N/A' : val ? 'Yes' : 'No'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Destinations */}
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Destinations</h2>
          {form.destinations.length === 0 && (
            <p className="text-sm text-gray-400">No destinations added yet.</p>
          )}
          {form.destinations.map((dest, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Client</label>
                  <select
                    value={dest.client_id}
                    onChange={e => setDestField(idx, 'client_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select client...</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Location (optional)</label>
                  <select
                    value={dest.location_id}
                    onChange={e => setDestField(idx, 'location_id', e.target.value)}
                    disabled={!dest.client_id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  >
                    <option value="">Any location</option>
                    {locationsFor(dest.client_id).map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {[loc.label, loc.city, loc.country].filter(Boolean).join(' — ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="button" onClick={() => removeDest(idx)} className="mt-6 p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={addDest} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Add destination
          </button>
        </div>

        {/* Travel days */}
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-800">Travel days</h2>
          <p className="text-xs text-gray-500">Check "Overnight (not paid by company)" only for nights where you paid for your own lodging — this claims the flat-rate allowance (prints "J"). Leave unchecked if the company paid, or if unsure (prints "N").</p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b">
                  <th className="text-left pb-2 pr-2">Date</th>
                  <th className="text-left pb-2 pr-2">Departure</th>
                  <th className="text-left pb-2 pr-2">End</th>
                  <th className="text-center pb-2 pr-1">Overnight<br/><span className="font-normal">not paid by company</span></th>
                  <th className="text-center pb-2 pr-1">Breakfast<br/><span className="font-normal">covered</span></th>
                  <th className="text-center pb-2 pr-1">Lunch<br/><span className="font-normal">covered</span></th>
                  <th className="text-center pb-2 pr-1">Dinner<br/><span className="font-normal">covered</span></th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody className="space-y-2">
                {form.days.map((day, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="py-2 pr-2">
                      <input
                        type="date"
                        value={day.date}
                        onChange={e => setDayField(idx, 'date', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm w-full focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="time"
                        value={day.departure_time}
                        onChange={e => setDayField(idx, 'departure_time', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm w-24 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="time"
                        value={day.end_time}
                        onChange={e => setDayField(idx, 'end_time', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm w-24 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </td>
                    <td className="py-2 pr-1 text-center">
                      <input
                        type="checkbox"
                        checked={day.overnight}
                        onChange={e => setDayField(idx, 'overnight', e.target.checked)}
                        className="w-4 h-4 rounded text-blue-600"
                      />
                    </td>
                    {['breakfast_covered', 'lunch_covered', 'dinner_covered'].map(field => (
                      <td key={field} className="py-2 pr-1 text-center">
                        <input
                          type="checkbox"
                          checked={day[field]}
                          onChange={e => setDayField(idx, field, e.target.checked)}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                      </td>
                    ))}
                    <td className="py-2">
                      {form.days.length > 1 && (
                        <button type="button" onClick={() => removeDay(idx)} className="p-1 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addDay} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Add day
          </button>
        </div>

        {/* Foreign travel */}
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_foreign_travel"
              checked={form.is_foreign_travel}
              onChange={e => setField('is_foreign_travel', e.target.checked)}
              className="w-4 h-4 rounded text-blue-600"
            />
            <label htmlFor="is_foreign_travel" className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Plane className="w-5 h-5 text-blue-500" />
              Foreign travel (outside Germany)
            </label>
          </div>

          {form.is_foreign_travel && (
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Left German territory (Grenzübertritt Hinreise)
                </label>
                <input
                  type="datetime-local"
                  value={form.border_crossing_outbound}
                  onChange={e => setField('border_crossing_outbound', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Returned to German territory (Grenzübertritt Rückreise)
                </label>
                <input
                  type="datetime-local"
                  value={form.border_crossing_return}
                  onChange={e => setField('border_crossing_return', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create trip'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/trips')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {isEdit && (
            <button
              type="button"
              disabled={deleting}
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete trip
            </button>
          )}
        </div>
      </form>

      {/* PDF generation panel — only shown when editing */}
      {isEdit && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Generate Travel Allowance PDF
            </h2>
            <button
              onClick={() => setShowPdfPanel(p => !p)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showPdfPanel ? 'Hide costs' : 'Show / edit costs'}
            </button>
          </div>

          {showPdfPanel && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'bus_amount', label: 'BUS / Public transport (€)' },
                { key: 'taxi_amount', label: 'Taxi / Uber (€)' },
                { key: 'nkso1_amount', label: 'Other expenses 1 (€)' },
                { key: 'nkso2_amount', label: 'Other expenses 2 (€)' },
                { key: 'park_amount', label: 'Parking (€)' },
                { key: 'bewk_amount', label: 'Entertainment (€)' },
                { key: 'km_amount', label: 'Fahrten mit dem eigenen PKW (km)', step: '1' },
              ].map(({ key, label, step }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <input
                    type="number"
                    step={step || '0.01'}
                    min="0"
                    value={costOverrides[key]}
                    onChange={e => setCostOverrides(o => ({ ...o, [key]: e.target.value }))}
                    placeholder="auto"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400">
            Values are computed from linked expenses. Edit any field to override; clear it to revert to computed.
          </p>

          <button
            type="button"
            disabled={pdfLoading}
            onClick={handleGeneratePdf}
            className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {pdfLoading && <Loader className="w-4 h-4 animate-spin" />}
            Generate travel allowance PDF
          </button>
        </div>
      )}
    </div>
  );
}

export default TripForm;
